/**
 * Column Factory Functions
 *
 * This module provides specialized column creation functions:
 * - createCustomColumn: Creates columns with custom rendering (customType or editable)
 * - createStandardColumnWithMeta: Creates columns with metadata (switch, textWeight, sizes)
 * - createLinkColumn: Creates navigation link columns
 * - createDrawerColumn: Creates drawer trigger columns
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { BaseColumn } from 'types/table';
import { DrawerCell } from 'features/misc/table';
import { Switch } from '@mui/material';
import { AccessControl } from 'features/accessControl';
import { Text } from 'components/text';
import { t } from '@lingui/macro';
import { trans } from '../../utils/index';
import { Link } from 'react-router-dom';
import { LinkCell } from '../styles';

import type {
  CustomColumnMeta,
  StandardColumnMeta,
  LinkColumnMeta,
  DrawerColumnMeta,
  CustomColumn,
} from '../types';
import { renderColumnCell, fieldTypeConfig } from './columnConfig';
import type { RendererOverrides } from './columnConfig';

// ============================================================================
// Constants
// ============================================================================

/** Column types that are inherently non-sortable (array/multi-value fields) */
const NON_SORTABLE_TYPES: readonly string[] = ['tags', 'multiselect'];

/**
 * Resolve the default enableSorting value for a column.
 * Array-based types (tags, multiselect) default to false; all others default to true.
 * An explicit enableSorting value on the config always takes precedence.
 */
const resolveEnableSorting = (enableSorting: boolean | undefined, type?: string): boolean =>
  enableSorting ?? !(type && NON_SORTABLE_TYPES.includes(type));

// ============================================================================
// Column Factory Functions
// ============================================================================

/**
 * Creates a custom column definition based on customType or editable pattern
 * Uses renderColumnCell as single entry point for all rendering
 */
export const createCustomColumn = <T extends Record<string, unknown>>(
  key: string,
  config: BaseColumn & CustomColumnMeta<T>,
  rendererOverrides?: RendererOverrides
): ColumnDef<T> => {
  // Get default sizes from fieldTypeConfig based on column type
  const typeConfig = config.type ? fieldTypeConfig[config.type] : undefined;

  // Extract size properties from config if they exist, falling back to fieldTypeConfig defaults
  const sizeProps: Partial<ColumnDef<T>> = {};
  const configWithSizes = config as BaseColumn &
    CustomColumnMeta<T> & { minSize?: number; size?: number; maxSize?: number };

  // Apply sizes: explicit config > fieldTypeConfig defaults
  sizeProps.minSize = configWithSizes.minSize ?? typeConfig?.minSize;
  sizeProps.size = configWithSizes.size ?? typeConfig?.size;
  sizeProps.maxSize = configWithSizes.maxSize ?? typeConfig?.maxSize;

  // Base column definition
  // Use provided accessorFn if available (e.g., for custom fields that extract from customFields array)
  // Otherwise, for dd type, create accessor that handles {id, name} objects
  // For other types, use accessorKey
  const baseCol: ColumnDef<T> = {
    ...(config.accessorFn
      ? { accessorFn: config.accessorFn }
      : config.type === 'dd' || config.type === 'multiselect'
        ? {
            accessorFn: (row: T) => {
              const value = (row as Record<string, unknown>)[key];
              return value && typeof value === 'object' && 'name' in value
                ? (value as { name: string }).name
                : value;
            },
          }
        : { accessorKey: config.accessorKey ?? key }),
    id: config.id,
    header: typeof config.label === 'string' ? (trans(config.label) as string) : config.label,
    enableSorting: resolveEnableSorting(config.enableSorting, config.type),
    enableResizing: config.customType === 'actions' ? false : undefined,
    // Default sizes for actions column if not specified
    minSize:
      config.customType === 'actions' && sizeProps.minSize === undefined ? 80 : sizeProps.minSize,
    size: config.customType === 'actions' && sizeProps.size === undefined ? 80 : sizeProps.size,
    maxSize:
      config.customType === 'actions' && sizeProps.maxSize === undefined ? 100 : sizeProps.maxSize,
  } as ColumnDef<T>;

  // If no customType AND not editable, return base column
  // Any column with editable: true needs custom rendering
  if (!config.customType && !config.editable) {
    return baseCol;
  }

  // Use renderColumnCell as single entry point for all rendering.
  // Editable state is read from context in cell components so we don't pass it here (keeps columns stable).
  const customRendering = renderColumnCell(
    config as CustomColumn<T & { id: number | string }>,
    {},
    rendererOverrides
  );

  // Merge custom rendering with base column
  return {
    ...baseCol,
    ...(customRendering as Partial<ColumnDef<T>>),
  } as ColumnDef<T>;
};

/**
 * Creates a standard column with metadata (switch, textWeight, sizes, etc.)
 */
export const createStandardColumnWithMeta = <T extends Record<string, unknown>>(
  key: string,
  config: BaseColumn & StandardColumnMeta<T>
): ColumnDef<T> => {
  // Get default sizes from fieldTypeConfig based on column type
  const typeConfig = config.type ? fieldTypeConfig[config.type] : undefined;

  const baseCol: ColumnDef<T> = {
    accessorKey: config.accessorKey ?? key,
    id: config.id,
    header: typeof config.label === 'string' ? (trans(config.label) as string) : config.label,
    enableSorting: resolveEnableSorting(config.enableSorting, config.type),
    minSize: config.minSize ?? typeConfig?.minSize,
    size: config.size ?? typeConfig?.size,
    maxSize: config.maxSize ?? typeConfig?.maxSize,
  };

  // Handle switch column
  if (config.switch) {
    const switchConfig = config.switch;
    return {
      ...baseCol,
      cell: ({ row }: CellContext<T, unknown>) => {
        const switchElement = (
          <Switch
            checked={switchConfig.checked(row.original)}
            onChange={() => switchConfig.onChange(row.original)}
            size="small"
          />
        );

        if (switchConfig.requiresAccess) {
          return (
            <AccessControl
              write={switchConfig.requiresAccess.write}
              scope={switchConfig.requiresAccess.scope}
              fallback={
                switchConfig.fallback ?? (
                  <Switch checked={switchConfig.checked(row.original)} disabled size="small" />
                )
              }
            >
              {switchElement}
            </AccessControl>
          );
        }

        return switchElement;
      },
      tooltipRender: cell => {
        return switchConfig.checked(cell.row.original) ? t`Active` : t`Inactive`;
      },
      enableSorting: false,
    };
  }

  // Handle text column with weight or primaryColumn (primaryColumn automatically gets bold)
  const effectiveTextWeight = config.primaryColumn ? 'bold' : config.textWeight;
  if (effectiveTextWeight && config.type === 'text') {
    return {
      ...baseCol,
      cell: ({ row }: CellContext<T, unknown>) => {
        const value = row.original[key as keyof T];
        return (
          <Text variant="bodySm" weight={effectiveTextWeight}>
            {String(value ?? '')}
          </Text>
        );
      },
      tooltipRender: cell => {
        const value = cell.row.original[key as keyof T];
        return String(value ?? '');
      },
    };
  }

  // Default: use standard rendering from generateColumns
  return baseCol;
};

/**
 * Creates a link column that renders as a navigation link
 */
export const createLinkColumn = <T extends Record<string, unknown>>(
  key: string,
  config: BaseColumn & { link: LinkColumnMeta; accessorKey?: string }
): ColumnDef<T> => {
  const { link } = config;
  const idField = link.idField ?? 'id';

  return {
    accessorKey: config.accessorKey ?? key,
    id: config.id,
    header: typeof config.label === 'string' ? (trans(config.label) as string) : config.label,
    enableSorting: true,
    cell: ({ row }: CellContext<T, unknown>) => {
      const value = row.original[key as keyof T];
      const id = row.original[idField as keyof T];
      const url = link.to.replace(':id', String(id ?? ''));

      return (
        <LinkCell>
          <Link
            to={url}
            target={link.newTab ? '_blank' : undefined}
            rel={link.newTab ? 'noopener noreferrer' : undefined}
          >
            {String(value ?? '')}
          </Link>
        </LinkCell>
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.row.original[key as keyof T];
      return String(value ?? '');
    },
    meta: {
      bold: true,
    },
  };
};

/**
 * Creates a drawer column that renders as a drawer trigger cell
 */
export const createDrawerColumn = <T extends Record<string, unknown>>(
  key: string,
  config: BaseColumn & { drawer: DrawerColumnMeta<T>; size?: number; accessorKey?: string }
): ColumnDef<T> => {
  const { drawer } = config;

  return {
    accessorKey: config.accessorKey ?? key,
    id: config.id,
    header: typeof config.label === 'string' ? (trans(config.label) as string) : config.label,
    enableSorting: true,
    size: config.size ?? 285,
    cell: ({ row }: CellContext<T, unknown>) => {
      const value = row.original[key as keyof T];
      const rowId = String(row.original.id ?? row.id);
      const avatarColor = drawer.avatarField ? String(row.original[drawer.avatarField] ?? '') : '';

      return (
        <DrawerCell
          text={String(value ?? '')}
          avatarText={String(value ?? '')}
          color={avatarColor}
          rowId={rowId}
          onOpen={id => drawer.onOpen?.(row.original, id)}
        />
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.row.original[key as keyof T];
      return String(value ?? '');
    },
  };
};
